import React, { useContext, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LanguageContext } from '../../App';
import { motion } from 'framer-motion';

const translations = {
  en: {
    title: 'Make a Donation',
    subtitle: 'Your contribution helps us make a difference',
    scan: 'Scan QR Code to donate',
    upi: 'UPI ID',
    upiId: 'radhekrishna.sewa@upi',
    close: 'Close',
    note: 'Every contribution, no matter how small, helps us create positive change.',
    bankDetails: 'Bank Details',
    accountName: 'Account Name: Radhe Krishna Sewa Samiti',
    accountNumber: 'Account Number: 06510200000725',
    bankName: 'Bank Name: Bank of Baroda',
    branch: 'Branch: Husaingunj Branch, Chhewka-212651',
    ifsc: 'IFSC Code: BARBOHUSAIN',
    qrError: 'QR code temporarily unavailable. Please use UPI ID or bank transfer.'
  },
  hi: {
    title: 'दान करें',
    subtitle: 'आपका योगदान हमें बदलाव लाने में मदद करता है',
    scan: 'दान करने के लिए QR कोड स्कैन करें',
    upi: 'यूपीआई आईडी',
    upiId: 'radhekrishna.sewa@upi',
    close: 'बंद करें',
    note: 'हर योगदान, चाहे कितना भी छोटा हो, हमें सकारात्मक बदलाव लाने में मदद करता है।',
    bankDetails: 'बैंक विवरण',
    accountName: 'खाता नाम: राधे कृष्णा सेवा समिति',
    accountNumber: 'खाता संख्या: 06510200000725',
    bankName: 'बैंक का नाम: बैंक ऑफ बड़ौदा',
    branch: 'शाखा: हुसैनगंज शाखा, छेवका-212651',
    ifsc: 'आईएफएससी कोड: BARBOHUSAIN',
    qrError: 'QR कोड अस्थायी रूप से उपलब्ध नहीं है। कृपया UPI ID या बैंक ट्रांसफर का उपयोग करें।'
  }
};

interface DonateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DonateModal: React.FC<DonateModalProps> = ({ isOpen, onClose }) => {
  const { language } = useContext(LanguageContext);
  const [qrError, setQrError] = useState(false);
  const t = translations[language];

  const handleQrError = () => {
    setQrError(true);
  };

  if (!isOpen) return null;

  return (
    <motion.div 
      className="fixed inset-0 z-[100] overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="flex min-h-screen items-end justify-center text-center sm:items-center sm:p-0">
        <motion.div 
          className="fixed inset-0 bg-black/75 transition-opacity" 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        <motion.div 
          className="relative inline-block w-full transform overflow-hidden rounded-t-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:rounded-xl sm:align-middle"
          initial={{ opacity: 0, y: "100%" }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                onClick={onClose}
              >
                <span className="sr-only">{t.close}</span>
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="text-center">
              <motion.h3 
                className="text-2xl font-bold text-gray-900 sm:text-3xl mb-2 font-baloo"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
              >
                {t.title}
              </motion.h3>
              <motion.p 
                className="text-lg text-gray-500 mb-8"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                {t.subtitle}
              </motion.p>

              <div className="space-y-8">
                <motion.div 
                  className="bg-gray-50 p-6 rounded-xl mx-auto max-w-sm"
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {!qrError ? (
                    <>
                      <img
                        src="/images/qr_manoj.jpeg"
                        alt="Donation QR Code"
                        className="w-48 h-48 mx-auto mb-4"
                        onError={handleQrError}
                      />
                      <div className="text-center">
                        <p className="text-lg font-medium text-gray-900 mb-2">
                          {t.scan}
                        </p>
                        <p className="text-gray-600">
                          {t.upi}: <span className="font-mono font-medium">{t.upiId}</span>
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="w-48 h-48 mx-auto flex items-center justify-center bg-gray-100 rounded-lg p-4">
                      <p className="text-gray-600 text-center">{t.qrError}</p>
                    </div>
                  )}
                </motion.div>

                <motion.div
                  className="bg-gray-50 p-6 rounded-xl mx-auto max-w-sm"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <p className="text-xl font-medium text-gray-900 mb-4">{t.bankDetails}</p>
                  <div className="space-y-3 text-gray-600">
                    <p className="font-medium">{t.accountName}</p>
                    <p className="font-mono">{t.accountNumber}</p>
                    <p>{t.bankName}</p>
                    <p>{t.branch}</p>
                    <p className="font-mono font-medium">{t.ifsc}</p>
                  </div>
                </motion.div>

                <motion.p 
                  className="text-sm text-gray-500 max-w-sm mx-auto"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  {t.note}
                </motion.p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default DonateModal; 